import { useEffect } from 'react';

/**
 * Forwards predominantly-vertical wheel input from x-scroll strips to the
 * page. Chrome latches Magic Mouse/trackpad gestures (which always carry
 * some deltaX jitter) onto the first scrollable-in-any-axis ancestor under
 * the cursor -- on a horizontal scroll strip that traps vertical page
 * scroll for the whole gesture while hovered (#1842). Horizontal intent
 * (|deltaX| >= |deltaY|) passes through untouched so strips still scroll
 * sideways; a strip that isn't currently overflowing (e.g. a layout that
 * toggles between a scroll strip and a grid) is left to native behaviour.
 *
 * React's synthetic onWheel is passive, so preventDefault must go through
 * a real addEventListener(..., { passive: false }) on the DOM node.
 * behavior: 'instant' is required -- 'auto' would inherit the global CSS
 * scroll-behavior: smooth and feel rubbery.
 *
 * @param {Array<import('react').RefObject<HTMLElement>>} refs - strip refs; null/unmounted refs are skipped
 * @param {Array<any>} deps - re-attach when strip content (and therefore scrollability) may have changed
 */
export function useWheelScrollPassthrough(refs, deps) {
  useEffect(() => {
    const strips = refs.map((ref) => ref.current).filter(Boolean);
    if (!strips.length) return undefined;

    const onWheel = (e) => {
      const el = e.currentTarget;
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        // deltaY is only pixels under the common DOM_DELTA_PIXEL mode (0).
        // Some browser/device combos report DOM_DELTA_LINE (1) or
        // DOM_DELTA_PAGE (2) -- passing those straight to scrollBy would
        // move the page by 1-3px per wheel notch instead of a real scroll.
        const deltaY = e.deltaMode === 1 ? e.deltaY * 16
          : e.deltaMode === 2 ? e.deltaY * window.innerHeight
          : e.deltaY;
        window.scrollBy({ top: deltaY, behavior: 'instant' });
      }
    };

    strips.forEach((el) => el.addEventListener('wheel', onWheel, { passive: false }));
    return () => strips.forEach((el) => el.removeEventListener('wheel', onWheel));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps supplied by caller, refs intentionally excluded (read via .current at attach time)
  }, deps);
}
